import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {styled} from "@mui/material/styles";

import Badge from "components/Badge";
import CustomTooltip from "components/CustomTooltip";
import FormattedNumberWithCurrency from "components/formattedNumberWithCurrencies/formattedNumberWithCurrency";
import {ReactComponent as InfoCircle} from "assets/img/info-circle.svg";
import {black500, green500, greyishBlue400, red500} from "assets/jss/main";

ListingPriceCell.propTypes = {
    // current (possibly discounted) price
    price: PropTypes.number,
    // pre-discount price: shown crossed out with the discount badge
    previousPrice: PropTypes.number,
    currency: PropTypes.string,
    // signed % vs the market benchmark ("Marché" line, hidden when null)
    priceCompetitiveness: PropTypes.number,
    // mobile article cell: prices and discount on one wrapping line instead of
    // the fixed-width column
    inline: PropTypes.bool,
};

const Container = styled('div', {shouldForwardProp: (prop) => 'inline' !== prop})(({inline}) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: inline ? '4px' : '12px',
    // 104px, fixed (DS "Sales Info Container", w-104px shrink-0).
    width: inline ? '100%' : '104px',
    minWidth: 0
}));

const PriceInfo = styled('div', {shouldForwardProp: (prop) => 'inline' !== prop})(({inline}) => ({
    display: 'flex',
    flexDirection: inline ? 'row' : 'column',
    alignItems: inline ? 'center' : 'flex-start',
    flexWrap: inline ? 'wrap' : 'nowrap',
    gap: '4px'
}));

const CurrentPrice = styled('div')({
    color: black500,
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap'
});

const PreviousPrice = styled('div')({
    color: greyishBlue400,
    fontSize: '12px',
    fontWeight: 300,
    textDecoration: 'line-through',
    whiteSpace: 'nowrap'
});

const Benchmark = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '12px',
    whiteSpace: 'nowrap'
});

const BenchmarkLabel = styled('span')({
    color: greyishBlue400
});

const BenchmarkValue = styled('span', {shouldForwardProp: (prop) => 'isOverpriced' !== prop})(({isOverpriced}) => ({
    color: isOverpriced ? red500 : green500,
    fontWeight: 600
}));

// styled() forwards the ref the tooltip needs to anchor itself
const InfoIcon = styled(InfoCircle)({
    width: '16px',
    height: '16px',
    flexShrink: 0,
    'path': {
        stroke: greyishBlue400
    }
});

const formatSignedPercent = (value) => `${value > 0 ? '+' : value < 0 ? '-' : ''} ${Math.abs(Math.round(value))} %`;

function ListingPriceCell({price, previousPrice, currency, priceCompetitiveness, inline = false}) {
    const hasDiscount = null !== previousPrice && undefined !== previousPrice && previousPrice > 0 && previousPrice !== price;
    const discountPercent = hasDiscount ? Math.round(((price - previousPrice) / previousPrice) * 100) : null;

    return (
        <Container inline={inline}>
            <PriceInfo inline={inline}>
                <CurrentPrice>
                    <FormattedNumberWithCurrency value={price} currency={currency}/>
                </CurrentPrice>
                {hasDiscount && (
                    <>
                        <PreviousPrice>
                            <FormattedNumberWithCurrency value={previousPrice} currency={currency}/>
                        </PreviousPrice>
                        {null !== discountPercent && 0 !== discountPercent && (
                            <Badge color="info" border>{formatSignedPercent(discountPercent)}</Badge>
                        )}
                    </>
                )}
            </PriceInfo>
            {null !== priceCompetitiveness && undefined !== priceCompetitiveness && (
                <Benchmark>
                    <BenchmarkLabel>
                        <FormattedMessage id={"retailer.product.list.price.benchmark"}/>
                    </BenchmarkLabel>
                    <BenchmarkValue isOverpriced={priceCompetitiveness > 0}>
                        {formatSignedPercent(priceCompetitiveness)}
                    </BenchmarkValue>
                    <CustomTooltip title={<FormattedMessage id={"retailer.product.list.price.benchmark.tooltip"}/>}>
                        <InfoIcon/>
                    </CustomTooltip>
                </Benchmark>
            )}
        </Container>
    );
}

export default ListingPriceCell;
